import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Building2, Search, Trophy } from "lucide-react";
import { api, cls, fmtAum, fmtPct } from "@/lib/api";

export default function AmcScorecard() {
  const [amcs, setAmcs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("total_aum_cr");

  useEffect(() => {
    api.get("/amcs").then((r) => {
      setAmcs(r.data.amcs);
      setLoading(false);
    });
  }, []);

  const filtered = useMemo(() => {
    let list = [...amcs];
    if (q) {
      const s = q.toLowerCase();
      list = list.filter((a) => a.amc.toLowerCase().includes(s));
    }
    list.sort((a, b) => {
      const av = a[sort];
      const bv = b[sort];
      if (av == null) return 1;
      if (bv == null) return -1;
      return bv - av;
    });
    return list;
  }, [amcs, q, sort]);

  const topPerformer = useMemo(() => {
    if (!amcs.length) return null;
    return amcs.reduce(
      (best, a) => (a.avg_return_1y != null && (best == null || a.avg_return_1y > best.avg_return_1y) ? a : best),
      null
    );
  }, [amcs]);

  const totalFunds = amcs.reduce((s, a) => s + a.fund_count, 0);
  const totalAum = amcs.reduce((s, a) => s + a.total_aum_cr, 0);

  return (
    <div className="max-w-7xl mx-auto px-6 py-8">
      <div className="mb-6">
        <div className="flex items-center gap-2 text-xs font-semibold text-sif-secondary uppercase tracking-widest">
          <Building2 className="w-3.5 h-3.5" /> AMC Scorecard
        </div>
        <h1 className="mt-2 text-3xl font-bold text-sif-primary">Fund houses running SIFs</h1>
        <p className="mt-2 text-sm text-sif-muted">
          AUM, fund count, average returns and top performer per AMC — ranked & searchable.
        </p>
      </div>

      {/* Aggregate stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <StatBox testId="amc-stat-total" label="Total AMCs" value={amcs.length} />
        <StatBox testId="amc-stat-funds" label="Total Funds" value={totalFunds} />
        <StatBox testId="amc-stat-aum" label="Combined AUM" value={fmtAum(totalAum)} accent="text-sif-secondary" />
        <StatBox
          testId="amc-stat-top"
          label="Top AMC · 1Y avg"
          value={topPerformer ? fmtPct(topPerformer.avg_return_1y) : "…"}
          sub={topPerformer?.amc}
          accent="text-sif-accent"
        />
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-sif p-4 card-shadow mb-6">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-sif-muted" />
            <input
              data-testid="amc-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search AMC…"
              className="w-full pl-10 pr-3 py-2.5 rounded-lg border border-sif bg-slate-50 text-sm focus:outline-none focus:ring-2 focus:ring-sif-primary/20 focus:bg-white transition"
            />
          </div>
          <select
            data-testid="amc-sort"
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            className="px-3 py-2.5 rounded-lg border border-sif bg-slate-50 text-sm text-sif-text"
          >
            <option value="total_aum_cr">Sort: AUM</option>
            <option value="fund_count">Sort: Fund Count</option>
            <option value="avg_return_1y">Sort: 1Y Avg Return</option>
            <option value="avg_return_1m">Sort: 1M Avg Return</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div
        data-testid="amc-table"
        className="bg-white rounded-xl border border-sif card-shadow overflow-hidden"
      >
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-sif">
              <tr className="text-left text-[11px] uppercase tracking-wider text-sif-muted">
                <th className="px-5 py-3 font-semibold">AMC</th>
                <th className="px-5 py-3 font-semibold text-right">Funds</th>
                <th className="px-5 py-3 font-semibold text-right">AUM</th>
                <th className="px-5 py-3 font-semibold text-right">1M avg</th>
                <th className="px-5 py-3 font-semibold text-right">3M avg</th>
                <th className="px-5 py-3 font-semibold text-right">1Y avg</th>
                <th className="px-5 py-3 font-semibold">Best 1Y fund</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={7} className="text-center py-16 text-sif-muted">Loading AMCs…</td>
                </tr>
              )}
              {!loading &&
                filtered.map((a, idx) => (
                  <tr
                    key={a.amc}
                    data-testid={`amc-row-${a.amc.replace(/\s+/g, "-")}`}
                    className="border-b border-sif last:border-0 hover:bg-slate-50 transition-colors"
                  >
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-3">
                        <div
                          className={`w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold ${
                            idx < 3 ? "bg-sif-accent/15 text-sif-accent" : "bg-slate-100 text-sif-muted"
                          }`}
                        >
                          {idx + 1}
                        </div>
                        <div>
                          <div className="font-semibold text-sif-primary">{a.amc}</div>
                          <div className="text-xs text-sif-muted mt-0.5 truncate max-w-[240px]">
                            {a.categories.slice(0, 3).join(" · ")}
                            {a.categories.length > 3 ? ` +${a.categories.length - 3}` : ""}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-4 text-right text-sm font-semibold text-sif-primary">
                      {a.fund_count}
                    </td>
                    <td className="px-5 py-4 text-right text-sm font-semibold text-sif-secondary">
                      {fmtAum(a.total_aum_cr)}
                    </td>
                    <td className={`px-5 py-4 text-right font-mono text-sm ${cls(a.avg_return_1m)}`}>
                      {fmtPct(a.avg_return_1m)}
                    </td>
                    <td className={`px-5 py-4 text-right font-mono text-sm ${cls(a.avg_return_3m)}`}>
                      {fmtPct(a.avg_return_3m)}
                    </td>
                    <td className={`px-5 py-4 text-right font-mono text-sm font-semibold ${cls(a.avg_return_1y)}`}>
                      {fmtPct(a.avg_return_1y)}
                    </td>
                    <td className="px-5 py-4">
                      {a.best_fund_1y ? (
                        <Link
                          to={`/fund/${a.best_fund_1y.code}`}
                          data-testid={`amc-best-fund-link-${a.amc.replace(/\s+/g, "-")}`}
                          className="inline-flex items-center gap-1.5 group"
                        >
                          <Trophy className="w-3.5 h-3.5 text-sif-accent" />
                          <span className="text-xs text-sif-primary group-hover:text-sif-secondary transition-colors">
                            {a.best_fund_1y.name.split("Long-Short")[0].trim()}
                          </span>
                          <span className={`text-xs font-mono font-semibold ${cls(a.best_fund_1y.return_1y)}`}>
                            {fmtPct(a.best_fund_1y.return_1y)}
                          </span>
                        </Link>
                      ) : (
                        <span className="text-xs text-sif-muted">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              {!loading && filtered.length === 0 && (
                <tr>
                  <td colSpan={7} className="text-center py-16 text-sif-muted">No AMCs match your search.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function StatBox({ label, value, sub, accent, testId }) {
  return (
    <div data-testid={testId} className="bg-white rounded-xl border border-sif p-5 card-shadow">
      <div className="text-xs text-sif-muted uppercase tracking-wider font-medium">{label}</div>
      <div className={`mt-2 text-2xl font-bold ${accent || "text-sif-primary"}`}>{value}</div>
      {sub && <div className="mt-1 text-xs text-sif-muted truncate">{sub}</div>}
    </div>
  );
}
